import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { 
  Save,
  RotateCcw,
  TestTube,
  CheckCircle,
  AlertTriangle,
  Loader,
  Eye,
  EyeOff,
  Copy,
  Key,
  MessageSquare,
  Target
} from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenantProtection } from '../../../hooks/useTenantProtection';
import { useTheme } from '../../../contexts/ThemeContext';

const LineConfiguration = ({ onConfigUpdate }) => {
  const { user } = useAuth();
  const { getProtectedTenantInfo } = useTenantProtection();
  const { isDark } = useTheme();
  
  const [lineConfig, setLineConfig] = useState({
    channels: {
      SalesBuddy: {
        enabled: false,
        channelId: '',
        channelSecret: '',
        accessToken: '',
        userAccessToken: '',
        lineId: '',
        qrCodeUrl: '',
        showAccessToken: false,
        showUserAccessToken: false,
        showSecret: false
      },
      SalesAssist: {
        enabled: false,
        channelId: '',
        channelSecret: '',
        accessToken: '',
        userAccessToken: '',
        lineId: '',
        qrCodeUrl: '',
        showAccessToken: false,
        showUserAccessToken: false,
        showSecret: false,
        allowNonRegisteredReply: false,
        groupAutoResponse: false  // Default: silent mode - only log to notes
      }
    }
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingWebhook, setTestingWebhook] = useState({ SalesBuddy: false, SalesAssist: false });
  const [webhookTestResults, setWebhookTestResults] = useState({ SalesBuddy: null, SalesAssist: null });

  const getCurrentTenant = () => {
    return getProtectedTenantInfo();
  };

  const loadConfiguration = async () => {
    try {
      setLoading(true);
      const tenant = getCurrentTenant();
      const backendUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${backendUrl}/api/line-config/tenant/${tenant.code}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Add UI state fields if not present
        if (data.channels) {
          if (data.channels.SalesBuddy) {
            data.channels.SalesBuddy.showAccessToken = data.channels.SalesBuddy.showAccessToken || false;
            data.channels.SalesBuddy.showUserAccessToken = data.channels.SalesBuddy.showUserAccessToken || false;
            data.channels.SalesBuddy.showSecret = data.channels.SalesBuddy.showSecret || false;
          }
          if (data.channels.SalesAssist) {
            data.channels.SalesAssist.showAccessToken = data.channels.SalesAssist.showAccessToken || false;
            data.channels.SalesAssist.showUserAccessToken = data.channels.SalesAssist.showUserAccessToken || false;
            data.channels.SalesAssist.showSecret = data.channels.SalesAssist.showSecret || false;
          }
        }
        setLineConfig(data);
      }
    } catch (error) {
      console.error('Failed to load Line configuration:', error);
      toast.error('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfiguration();
  }, []);

  const saveConfiguration = async () => {
    try {
      setSaving(true);
      const tenant = getCurrentTenant();
      const backendUrl = process.env.REACT_APP_API_URL || '';
      
      // Remove UI-only fields before saving
      const configToSave = {
        channels: {
          SalesBuddy: { ...lineConfig.channels.SalesBuddy },
          SalesAssist: { ...lineConfig.channels.SalesAssist }
        }
      };
      delete configToSave.channels.SalesBuddy.showAccessToken;
      delete configToSave.channels.SalesBuddy.showUserAccessToken;
      delete configToSave.channels.SalesBuddy.showSecret;
      delete configToSave.channels.SalesAssist.showAccessToken;
      delete configToSave.channels.SalesAssist.showUserAccessToken;
      delete configToSave.channels.SalesAssist.showSecret;
      
      const response = await fetch(`${backendUrl}/api/line-config/tenant/${tenant.code}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(configToSave)
      });

      if (response.ok) {
        toast.success('Line configuration saved successfully');
        if (onConfigUpdate) onConfigUpdate();
      } else {
        throw new Error('Failed to save configuration');
      }
    } catch (error) {
      console.error('Failed to save Line configuration:', error);
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const updateSalesBuddyConfig = (field, value) => {
    setLineConfig(prev => ({
      ...prev,
      channels: {
        ...prev.channels,
        SalesBuddy: {
          ...prev.channels?.SalesBuddy,
          [field]: value
        }
      }
    }));
  };

  const updateSalesAssistConfig = (field, value) => {
    setLineConfig(prev => ({
      ...prev,
      channels: {
        ...prev.channels,
        SalesAssist: {
          ...prev.channels?.SalesAssist,
          [field]: value
        }
      }
    }));
  };

  const toggleSalesBuddyEnabled = () => {
    setLineConfig(prev => ({
      ...prev,
      channels: {
        ...prev.channels,
        SalesBuddy: {
          ...prev.channels?.SalesBuddy,
          enabled: !prev.channels?.SalesBuddy?.enabled
        }
      }
    }));
  };

  const toggleSalesAssistEnabled = () => {
    setLineConfig(prev => ({
      ...prev,
      channels: {
        ...prev.channels,
        SalesAssist: {
          ...prev.channels?.SalesAssist,
          enabled: !prev.channels?.SalesAssist?.enabled
        }
      }
    }));
  };

  const handleQrCodeUpload = async (event, type = 'SalesBuddy') => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('qrCode', file);
      const tenant = getCurrentTenant();
      const tenantCode = tenant.code || 'DEMO';
      
      const backendUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${backendUrl}/api/line-config/upload-qr-code?tenantCode=${tenantCode}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        if (type === 'SalesAssist') {
          updateSalesAssistConfig('qrCodeUrl', result.data.qrCodeUrl);
        } else {
          updateSalesBuddyConfig('qrCodeUrl', result.data.qrCodeUrl);
        }
        toast.success('QR Code uploaded successfully');
      } else {
        throw new Error('Failed to upload QR code');
      }
    } catch (error) {
      console.error('Failed to upload QR code:', error);
      toast.error('Failed to upload QR code');
    }
  };

  const testLineWebhook = async (type = 'SalesBuddy') => {
    try {
      setTestingWebhook(prev => ({ ...prev, [type]: true }));
      const currentTenant = getCurrentTenant();
      const backendUrl = process.env.REACT_APP_API_URL || '';
      const endpoint = type === 'SalesAssist'
        ? `${backendUrl}/api/webhook/line/sales-assist/${currentTenant.code}`
        : `${backendUrl}/api/webhook/line/salesbuddy/${currentTenant.code}`;
      
      const healthResponse = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (healthResponse.ok) {
        const healthData = await healthResponse.json();
        setWebhookTestResults(prev => ({
          ...prev,
          [type]: {
            success: true,
            message: `Webhook endpoint is active and configured`,
            details: healthData,
            timestamp: new Date().toISOString()
          }
        }));
        toast.success(`${type === 'SalesAssist' ? 'SalesAssist' : 'SalesBuddy'} webhook is working!`);
      } else {
        throw new Error(`Webhook health check failed with status ${healthResponse.status}`);
      }
    } catch (error) {
      console.error('Webhook test failed:', error);
      setWebhookTestResults(prev => ({
        ...prev,
        [type]: {
          success: false,
          message: error.message,
          timestamp: new Date().toISOString()
        }
      }));
      toast.error(`Webhook test failed: ${error.message}`);
    } finally {
      setTestingWebhook(prev => ({ ...prev, [type]: false }));
    }
  };

  const generateLineUserToken = async (type = 'SalesBuddy') => {
    try {
      const currentTenant = getCurrentTenant();
      const backendUrl = process.env.REACT_APP_API_URL || '';
      // Note: OAuth endpoints may still be in ai-config for now, update later if needed
      const endpoint = type === 'SalesAssist' 
        ? `${backendUrl}/api/ai-config/line/power-sales/generate-user-token/${currentTenant.code}`
        : `${backendUrl}/api/ai-config/line/generate-user-token/${currentTenant.code}`;
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        
        if (result.status === 'success' && result.data?.oauthUrl) {
          const oauthWindow = window.open(
            result.data.oauthUrl,
            'line_oauth',
            'width=500,height=600,scrollbars=yes,resizable=yes'
          );
          
          if (!oauthWindow) {
            toast.error('Popup blocked. Please allow popups and try again.');
            return;
          }
          
          toast.success(`OAuth flow initiated for ${type === 'SalesAssist' ? 'SalesAssist' : 'SalesBuddy'}.`);
          
          const checkOAuthCompletion = setInterval(() => {
            if (oauthWindow.closed) {
              clearInterval(checkOAuthCompletion);
              loadConfiguration();
              toast.success('Line User Access Token generated successfully!');
            }
          }, 1000);
          
          setTimeout(() => {
            clearInterval(checkOAuthCompletion);
            if (!oauthWindow.closed) {
              oauthWindow.close();
              toast.error('OAuth flow timed out. Please try again.');
            }
          }, 600000);
        } else {
          throw new Error(result.message || 'Failed to generate OAuth URL');
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to generate OAuth URL');
      }
    } catch (error) {
      console.error('Failed to generate Line User Access Token:', error);
      toast.error(`Failed to generate User Access Token: ${error.message}`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const currentTenant = getCurrentTenant();

  const renderLineChannelConfig = (type = 'SalesBuddy') => {
    const isSalesAssist = type === 'SalesAssist';
    const config = isSalesAssist ? lineConfig.channels?.SalesAssist : lineConfig.channels?.SalesBuddy;
    const updateConfig = isSalesAssist ? updateSalesAssistConfig : updateSalesBuddyConfig;
    const toggleEnabled = isSalesAssist ? toggleSalesAssistEnabled : toggleSalesBuddyEnabled;
    const icon = isSalesAssist ? Target : MessageSquare;
    const Icon = icon;
    const color = isSalesAssist ? 'purple' : 'green';
    const title = isSalesAssist ? 'Line SalesAssist' : 'Line SalesBuddy';
    const description = isSalesAssist 
      ? 'Configure Line Official Account for SalesAssist integration'
      : 'Configure Line Official Account for SalesBuddy integration';

    return (
      <div className={`rounded-lg shadow ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="p-6">
          <div className={`border rounded-lg p-6 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-start space-x-4">
                <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-700' : `bg-${color}-100`}`}>
                  <Icon className={`h-6 w-6 text-${color}-600`} />
                </div>
                <div className="flex-1">
                  <h3 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{title}</h3>
                  <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{description}</p>
                </div>
              </div>
              <div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={config?.enabled || false}
                    onChange={toggleEnabled}
                    className={`rounded text-${color}-600 focus:ring-${color}-500 ${isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-300'}`}
                  />
                  <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Enabled</span>
                </label>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Line Configuration Fields */}
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Line Channel ID
                  </label>
                  <input
                    type="text"
                    value={config?.channelId || ''}
                    onChange={(e) => updateConfig('channelId', e.target.value)}
                    disabled={!config?.enabled}
                    className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-${color}-500 focus:border-${color}-500 ${
                      isDark 
                        ? 'border-gray-600 bg-gray-700 text-white disabled:bg-gray-800' 
                        : 'border-gray-300 bg-white text-gray-900 disabled:bg-gray-50'
                    }`}
                    placeholder="Enter Line Channel ID"
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Line Channel Secret
                  </label>
                  <div className="relative">
                    <input
                      type={config?.showSecret ? 'text' : 'password'}
                      value={config?.channelSecret || ''}
                      onChange={(e) => updateConfig('channelSecret', e.target.value)}
                      disabled={!config?.enabled}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-${color}-500 focus:border-${color}-500 pr-10 ${
                        isDark 
                          ? 'border-gray-600 bg-gray-700 text-white disabled:bg-gray-800' 
                          : 'border-gray-300 bg-white text-gray-900 disabled:bg-gray-50'
                      }`}
                      placeholder="Enter Line Channel Secret"
                    />
                    <span className="absolute inset-y-0 right-0 pr-3 flex items-center">
                      {config?.showSecret ? (
                        <EyeOff className="h-4 w-4 text-gray-400 cursor-pointer" onClick={() => updateConfig('showSecret', false)} />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-400 cursor-pointer" onClick={() => updateConfig('showSecret', true)} />
                      )}
                    </span>
                  </div>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Line Access Token
                  </label>
                  <div className="relative">
                    <input
                      type={config?.showAccessToken ? 'text' : 'password'}
                      value={config?.accessToken || ''}
                      onChange={(e) => updateConfig('accessToken', e.target.value)}
                      disabled={!config?.enabled}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-${color}-500 focus:border-${color}-500 pr-10 ${
                        isDark 
                          ? 'border-gray-600 bg-gray-700 text-white disabled:bg-gray-800' 
                          : 'border-gray-300 bg-white text-gray-900 disabled:bg-gray-50'
                      }`}
                      placeholder="Enter Line Channel Access Token"
                    />
                    <span className="absolute inset-y-0 right-0 pr-3 flex items-center">
                      {config?.showAccessToken ? (
                        <EyeOff className="h-4 w-4 text-gray-400 cursor-pointer" onClick={() => updateConfig('showAccessToken', false)} />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-400 cursor-pointer" onClick={() => updateConfig('showAccessToken', true)} />
                      )}
                    </span>
                  </div>
                  <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Channel Access Token for sending messages via Line API
                  </p>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Line User Access Token
                  </label>
                  <div className="relative">
                    <input
                      type={config?.showUserAccessToken ? 'text' : 'password'}
                      value={config?.userAccessToken || ''}
                      onChange={(e) => updateConfig('userAccessToken', e.target.value)}
                      disabled={!config?.enabled}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-${color}-500 focus:border-${color}-500 pr-10 ${
                        isDark 
                          ? 'border-gray-600 bg-gray-700 text-white disabled:bg-gray-800' 
                          : 'border-gray-300 bg-white text-gray-900 disabled:bg-gray-50'
                      }`}
                      placeholder="Enter Line User Access Token"
                    />
                    <span className="absolute inset-y-0 right-0 pr-3 flex items-center">
                      {config?.showUserAccessToken ? (
                        <EyeOff className="h-4 w-4 text-gray-400 cursor-pointer" onClick={() => updateConfig('showUserAccessToken', false)} />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-400 cursor-pointer" onClick={() => updateConfig('showUserAccessToken', true)} />
                      )}
                    </span>
                  </div>
                  <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    User Access Token for fetching profile information (optional)
                  </p>
                  <div className="mt-2">
                    <button
                      type="button"
                      onClick={() => generateLineUserToken(type)}
                      disabled={!config?.enabled || !config?.channelId || !config?.channelSecret}
                      className={`flex items-center space-x-2 px-3 py-1.5 text-sm rounded-md transition-colors ${
                        config?.enabled && config?.channelId && config?.channelSecret
                          ? `bg-${color}-600 text-white hover:bg-${color}-700 disabled:opacity-50`
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      <Key className="h-3 w-3" />
                      <span>Generate User Access Token</span>
                    </button>
                  </div>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Line ID (@username)
                  </label>
                  <input
                    type="text"
                    value={config?.lineId || ''}
                    onChange={(e) => updateConfig('lineId', e.target.value)}
                    disabled={!config?.enabled}
                    className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-${color}-500 focus:border-${color}-500 ${
                      isDark 
                        ? 'border-gray-600 bg-gray-700 text-white disabled:bg-gray-800' 
                        : 'border-gray-300 bg-white text-gray-900 disabled:bg-gray-50'
                    }`}
                    placeholder="Enter Line ID (e.g., @yourlineid)"
                  />
                </div>
              </div>

              {/* QR Code Upload */}
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    QR Code Image
                  </label>
                  <div className="space-y-2">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleQrCodeUpload(e, type)}
                      disabled={!config?.enabled}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-${color}-500 focus:border-${color}-500 ${
                        isDark 
                          ? 'border-gray-600 bg-gray-700 text-white disabled:bg-gray-800' 
                          : 'border-gray-300 bg-white text-gray-900 disabled:bg-gray-50'
                      }`}
                    />
                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      Upload QR code image for Line Official Account (max 5MB)
                    </p>
                  </div>
                </div>

                {/* QR Code Preview */}
                {config?.qrCodeUrl && (
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      QR Code Preview
                    </label>
                    <div className={`border rounded-lg p-4 ${isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-gray-50'}`}>
                      <img
                        src={config.qrCodeUrl}
                        alt="Line QR Code"
                        className="w-32 h-32 mx-auto object-contain"
                      />
                      <p className={`text-xs text-center mt-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        Scan this QR code to add Line Official Account
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* SalesAssist Specific: Allow Non-Registered Reply */}
            {isSalesAssist && (
              <div className={`mt-4 p-4 rounded-lg ${isDark ? 'bg-blue-900/20 border-blue-700' : 'bg-blue-50 border-blue-200'}`}>
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    checked={config?.allowNonRegisteredReply || false}
                    onChange={(e) => updateConfig('allowNonRegisteredReply', e.target.checked)}
                    disabled={!config?.enabled}
                    className={`mt-1 rounded text-${color}-600 focus:ring-${color}-500 ${isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-300'}`}
                  />
                  <div className="flex-1">
                    <label className={`block text-sm font-medium ${isDark ? 'text-blue-300' : 'text-blue-800'}`}>
                      Allow to reply for non-registered user
                    </label>
                    <p className={`text-xs mt-1 ${isDark ? 'text-blue-400' : 'text-blue-700'}`}>
                      ⚠️ AI token will be account on all conversation
                    </p>
                    <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      {config?.allowNonRegisteredReply 
                        ? 'SalesAssist will reply to anyone, including non-registered users. AI tokens will be consumed for all conversations.'
                        : 'SalesAssist will only reply to registered users. Non-registered users will be asked to register first.'
                      }
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* SalesAssist Specific: Group Auto Response */}
            {isSalesAssist && (
              <div className={`mt-4 p-4 rounded-lg ${isDark ? 'bg-purple-900/20 border-purple-700' : 'bg-purple-50 border-purple-200'}`}>
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    checked={config?.groupAutoResponse || false}
                    onChange={(e) => updateConfig('groupAutoResponse', e.target.checked)}
                    disabled={!config?.enabled}
                    className={`mt-1 rounded text-${color}-600 focus:ring-${color}-500 ${isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-300'}`}
                  />
                  <div className="flex-1">
                    <label className={`block text-sm font-medium ${isDark ? 'text-purple-300' : 'text-purple-800'}`}>
                      Enable Auto Response in Line Groups
                    </label>
                    <p className={`text-xs mt-1 ${isDark ? 'text-purple-400' : 'text-purple-700'}`}>
                      ⚠️ AI token will be consumed for group conversations
                    </p>
                    <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      {config?.groupAutoResponse 
                        ? 'SalesAssist will automatically respond to messages in Line groups. AI tokens will be consumed for all group conversations.'
                        : 'SalesAssist will stay silent in Line groups and only log conversations to notes. This is the recommended default setting.'
                      }
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Configuration Status */}
            <div className={`mt-4 p-3 rounded-lg ${
              config?.enabled && config?.channelId && config?.channelSecret && config?.accessToken
                ? isDark ? 'bg-green-900/20 border-green-700' : 'bg-green-50 border-green-200'
                : isDark ? 'bg-yellow-900/20 border-yellow-700' : 'bg-yellow-50 border-yellow-200'
            }`}>
              <div className="flex items-center space-x-2">
                {config?.enabled && config?.channelId && config?.channelSecret && config?.accessToken ? (
                  <CheckCircle className="h-4 w-4 text-green-600" />
                ) : (
                  <AlertTriangle className="h-4 w-4 text-yellow-600" />
                )}
                <span className={`text-sm font-medium ${
                  config?.enabled && config?.channelId && config?.channelSecret && config?.accessToken
                    ? isDark ? 'text-green-300' : 'text-green-800'
                    : isDark ? 'text-yellow-300' : 'text-yellow-800'
                }`}>
                  {config?.enabled && config?.channelId && config?.channelSecret && config?.accessToken
                    ? `${title} Configuration Complete`
                    : `${title} Configuration Incomplete`
                  }
                </span>
              </div>
            </div>

            {/* Webhook Endpoint Display */}
            {config?.enabled && (
              <div className={`mt-4 p-4 rounded-lg ${isDark ? `bg-${color}-900/20 border-${color}-700` : `bg-${color}-50 border-${color}-200`}`}>
                <h4 className={`text-sm font-medium mb-3 flex items-center space-x-2 ${isDark ? `text-${color}-300` : `text-${color}-800`}`}>
                  <Icon className="h-4 w-4" />
                  <span>Webhook Endpoint for Line Developer Console</span>
                </h4>
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <span className={`text-xs font-medium ${isDark ? `text-${color}-400` : `text-${color}-700`}`}>Webhook URL:</span>
                    <code className={`flex-1 px-2 py-1 text-xs rounded ${isDark ? `bg-gray-800 text-${color}-300` : `bg-gray-100 text-${color}-800`}`}>
                      {`${window.location.origin}/api/webhook/line/${isSalesAssist ? 'sales-assist' : 'salesbuddy'}/${currentTenant.code}`}
                    </code>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}/api/webhook/line/${isSalesAssist ? 'sales-assist' : 'salesbuddy'}/${currentTenant.code}`);
                        toast.success('Webhook URL copied to clipboard');
                      }}
                      className={`p-1 rounded hover:bg-${color}-200 ${isDark ? `hover:bg-${color}-800` : ''}`}
                      title="Copy to clipboard"
                    >
                      <Copy className={`h-3 w-3 text-${color}-600`} />
                    </button>
                  </div>
                  <p className={`text-xs ${isDark ? `text-${color}-400` : `text-${color}-700`}`}>
                    Configure this URL in your Line Developer Console → Messaging API → Webhook URL
                  </p>
                  
                  {/* Test Webhook Button */}
                  <div className="flex items-center space-x-2 pt-2">
                    <button
                      onClick={() => testLineWebhook(type)}
                      disabled={testingWebhook[type]}
                      className={`flex items-center space-x-2 px-3 py-1.5 text-xs rounded-md transition-colors ${
                        testingWebhook[type]
                          ? 'bg-gray-400 text-white cursor-not-allowed'
                          : `bg-${color}-600 text-white hover:bg-${color}-700`
                      }`}
                    >
                      {testingWebhook[type] ? (
                        <>
                          <Loader className="h-3 w-3 animate-spin" />
                          <span>Testing...</span>
                        </>
                      ) : (
                        <>
                          <TestTube className="h-3 w-3" />
                          <span>Test Webhook</span>
                        </>
                      )}
                    </button>
                    {webhookTestResults[type] && (
                      <div className="flex items-center space-x-1">
                        {webhookTestResults[type].success ? (
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        ) : (
                          <AlertTriangle className="h-4 w-4 text-red-600" />
                        )}
                        <span className={`text-xs ${
                          webhookTestResults[type].success 
                            ? 'text-green-600 dark:text-green-400' 
                            : 'text-red-600 dark:text-red-400'
                        }`}>
                          {webhookTestResults[type].message}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Action Buttons */}
      <div className="flex justify-end space-x-3">
        <button
          onClick={loadConfiguration}
          className={`flex items-center space-x-2 px-4 py-2 border rounded-md ${
            isDark 
              ? 'border-gray-600 text-gray-300 bg-gray-700 hover:bg-gray-600' 
              : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
          }`}
        >
          <RotateCcw className="h-4 w-4" />
          <span>Refresh</span>
        </button>
        <button
          onClick={saveConfiguration}
          disabled={saving}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? (
            <Loader className="h-4 w-4 animate-spin" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          <span>{saving ? 'Saving...' : 'Save Configuration'}</span>
        </button>
      </div>

      {/* SalesBuddy Configuration */}
      {renderLineChannelConfig('SalesBuddy')}

      {/* SalesAssist Configuration */}
      {renderLineChannelConfig('SalesAssist')}
    </div>
  );
};

export default LineConfiguration;

